/**
 * Account Security Service
 *
 * How an account is signed in to, managed from settings: "confirm it's you",
 * connecting and disconnecting Google or Apple, a first password, signing out
 * other devices, changing the email once both addresses agree, a new link to
 * verify it, and setting up two-factor on the website.
 *
 * Every call goes through `apiClient`, so a refusal reaches the caller as a
 * typed `ApiErrorClass`. Connecting, disconnecting, a first password and asking
 * to change the email answer `ConfirmationRequiredError` (423) until the
 * session or bearer has confirmed in the last ten minutes: confirm, then
 * retry. A call that answers the session user returns it for the caller to
 * write into the auth store.
 */

import { apiClient } from './api/client';
import { getAuthBaseUrl } from '../config/api';
import { HTTP_STATUS } from '../config/constants';
import { getAuthConfig } from '../di/container';
import { API_ENDPOINTS } from '../types/api.types';
import '../utils/string-extensions';

import type { SuccessApiResponse } from '../types/api.types';
import type {
  AddPasswordRequest,
  ConfirmationProof,
  IdentityConfirmation,
  LinkAppleRequest,
  LinkGoogleRequest,
  SocialProvider,
  TwoFactorQrCode,
  User,
  VerificationLinkResult,
} from '../types/auth';

/**
 * A route at the API root, outside the `/api` base `apiClient` is built on:
 * Fortify's, Sanctum's and the website's own. The URL is absolute, so the
 * client's base is ignored.
 */
const atApiRoot = (path: string): string => `${getAuthBaseUrl()}${path}`;

export class AccountSecurityService {
  /**
   * What confirming takes, and until when the session or bearer is confirmed
   * (`confirmed_until` is null when it isn't now).
   */
  async getConfirmation(): Promise<IdentityConfirmation> {
    const response = await apiClient.get<
      SuccessApiResponse<IdentityConfirmation>
    >(API_ENDPOINTS.GET_IDENTITY_CONFIRMATION.path);

    return response.data.data;
  }

  /**
   * Confirm it's you with one proof from `methods`, for ten minutes, and a code
   * or a recovery code when two-factor is on. A refused proof is a 422 under
   * its field (`password`, `code`, `recovery_code`, `id_token` or
   * `identity_token`), never a 401. A provider proof is a fresh sign-in with
   * the platform's SDK: never resend one.
   */
  async confirm(proof: ConfirmationProof): Promise<IdentityConfirmation> {
    const response = await apiClient.post<
      SuccessApiResponse<IdentityConfirmation>
    >(API_ENDPOINTS.CONFIRM_IDENTITY.path, proof);

    return response.data.data;
  }

  /**
   * Connect Google from an app. 409 when that Google account signs in to
   * another account (it is never moved), a different Google account is
   * connected already, or two-factor is on.
   */
  async linkGoogle(request: LinkGoogleRequest): Promise<User> {
    const response = await apiClient.post<SuccessApiResponse<User>>(
      API_ENDPOINTS.LINK_GOOGLE.path,
      request
    );

    return response.data.data;
  }

  /**
   * Connect Apple from the iOS app. Refused as `linkGoogle`, and 503 when
   * Apple can't be reached.
   */
  async linkApple(request: LinkAppleRequest): Promise<User> {
    const response = await apiClient.post<SuccessApiResponse<User>>(
      API_ENDPOINTS.LINK_APPLE.path,
      request
    );

    return response.data.data;
  }

  /**
   * Disconnect a provider: the devices signed in through it are signed out.
   * 404 when it isn't connected; 409 when it's the only way in, or it's Apple
   * while the email is Apple's relay.
   */
  async unlink(provider: SocialProvider): Promise<User> {
    const response = await apiClient.delete<SuccessApiResponse<User>>(
      API_ENDPOINTS.UNLINK_PROVIDER.path.replaceParam({ provider })
    );

    return response.data.data;
  }

  /**
   * A passwordless account's first password, under registration's rules (422
   * under `password`). 409 when the account has one already: changing it is
   * `PUT /user/password`.
   */
  async addPassword(request: AddPasswordRequest): Promise<User> {
    const response = await apiClient.post<SuccessApiResponse<User>>(
      API_ENDPOINTS.ADD_PASSWORD.path,
      request
    );

    return response.data.data;
  }

  /**
   * Sign out every other device: their bearers are deleted, whatever they
   * signed in with, and remember-me is cycled for every browser.
   */
  async signOutOtherDevices(): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SIGN_OUT_OTHER_DEVICES.path);
  }

  /**
   * Ask to change the email. It waits in `pending_email` until the current
   * address approves and the new one confirms. 422 under `email` (invalid,
   * another account's, or the current one); 409 while the current address is
   * Apple's relay with forwarding off, since the approval couldn't reach it.
   */
  async requestEmailChange(email: string): Promise<User> {
    const response = await apiClient.post<SuccessApiResponse<User>>(
      API_ENDPOINTS.REQUEST_EMAIL_CHANGE.path,
      { email }
    );

    return response.data.data;
  }

  /** Drop a pending email change. Its links stop working. */
  async cancelEmailChange(): Promise<User> {
    const response = await apiClient.delete<SuccessApiResponse<User>>(
      API_ENDPOINTS.CANCEL_EMAIL_CHANGE.path
    );

    return response.data.data;
  }

  /**
   * Approve an email change with the token the current address was sent. No
   * session needed: the token is the credential. Returns the new address,
   * which the confirmation went to. 404 when the link was used, is over an
   * hour old, or its request was replaced or cancelled.
   */
  async approveEmailChange(token: string): Promise<string> {
    await this.ensureCsrfCookie();

    const response = await apiClient.post<
      SuccessApiResponse<{ email: string }>
    >(API_ENDPOINTS.APPROVE_EMAIL_CHANGE.path, { token });

    return response.data.data.email;
  }

  /**
   * Confirm an email change with the token the new address was sent: the
   * account's email becomes it, and it's returned. Refused as
   * `approveEmailChange`, and 409 when another account took the address
   * meanwhile.
   */
  async confirmEmailChange(token: string): Promise<string> {
    await this.ensureCsrfCookie();

    const response = await apiClient.post<
      SuccessApiResponse<{ email: string }>
    >(API_ENDPOINTS.CONFIRM_EMAIL_CHANGE.path, { token });

    return response.data.data.email;
  }

  /**
   * Email the account a new link to verify its address. An unverified account
   * may still ask. The website's route sits at the API root and refuses a
   * bearer, so the apps use the same path under `/api`. Six a minute
   * (`RATE_LIMITED`).
   */
  async sendVerificationLink(): Promise<VerificationLinkResult> {
    const { path } = API_ENDPOINTS.RESEND_VERIFICATION;
    const url = getAuthConfig().mode === 'session' ? atApiRoot(path) : path;

    const response = await apiClient.post(url);

    return response.status === HTTP_STATUS.NO_CONTENT
      ? 'already-verified'
      : 'sent';
  }

  // Two-factor setup: Fortify's routes, for the website's session only. Each
  // answers `ConfirmationRequiredError` (423) without a confirmation from the
  // last ten minutes, and once two-factor is on, confirming takes a code.

  /**
   * Start setting two-factor up: a secret and eight recovery codes are made,
   * but two-factor isn't on until `confirmTwoFactor`. A secret that was never
   * confirmed is kept, so starting again shows the same QR code. 409 while
   * Google or Apple is connected.
   */
  async enableTwoFactor(): Promise<void> {
    await apiClient.post(atApiRoot(API_ENDPOINTS.TWO_FACTOR_ENABLE.path));
  }

  /** The QR code to scan, or null when there's no secret to show. */
  async twoFactorQrCode(): Promise<TwoFactorQrCode | null> {
    const response = await apiClient.get<TwoFactorQrCode | []>(
      atApiRoot(API_ENDPOINTS.TWO_FACTOR_QR_CODE.path)
    );

    return Array.isArray(response.data) ? null : response.data;
  }

  /** The setup key, for typing into an authenticator app. 404 with no secret. */
  async twoFactorSecretKey(): Promise<string> {
    const response = await apiClient.get<{ secretKey: string }>(
      atApiRoot(API_ENDPOINTS.TWO_FACTOR_SECRET_KEY.path)
    );

    return response.data.secretKey;
  }

  /**
   * Turn two-factor on with a code from the authenticator app. A wrong code is
   * a 422 under `code`; 409 while Google or Apple is connected.
   */
  async confirmTwoFactor(code: string): Promise<void> {
    await apiClient.post(atApiRoot(API_ENDPOINTS.TWO_FACTOR_CONFIRM.path), {
      code,
    });
  }

  /** The recovery codes, each good once. Empty while two-factor is off. */
  async recoveryCodes(): Promise<string[]> {
    const response = await apiClient.get<string[]>(
      atApiRoot(API_ENDPOINTS.TWO_FACTOR_RECOVERY_CODES.path)
    );

    return response.data;
  }

  /**
   * Replace the recovery codes: the old ones stop working. Read the new ones
   * with `recoveryCodes`.
   */
  async regenerateRecoveryCodes(): Promise<void> {
    await apiClient.post(
      atApiRoot(API_ENDPOINTS.TWO_FACTOR_REGENERATE_RECOVERY_CODES.path)
    );
  }

  /** Turn two-factor off. */
  async disableTwoFactor(): Promise<void> {
    await apiClient.delete(atApiRoot(API_ENDPOINTS.TWO_FACTOR_DISABLE.path));
  }

  /**
   * The emailed links open where nobody may be signed in, and a browser with
   * no session has no XSRF cookie yet, so Sanctum's CSRF check would refuse
   * the POST. Fetch one first, as `AuthService.resetPassword` does for a
   * visitor who lands on /forgot-password. Token mode sends no cookies, so
   * there is nothing to fetch.
   */
  private async ensureCsrfCookie(): Promise<void> {
    if (getAuthConfig().mode !== 'session') return;

    await apiClient.get(atApiRoot(API_ENDPOINTS.CSRF_INIT.path));
  }
}

export const accountSecurityService = new AccountSecurityService();
